/** Permit & appraiser portal health matrix (Tom 2026-08-13: "a section in the
 * admin for all the county city and permit websites with health check"). The
 * worker probes every ~30 min: county appraiser portals, CITY permit portals
 * (Tom 2026-08-14: "county AND city permits check"), core data services, and
 * the LIVE paid-fetch path (fetch_backend + Decodo residential — NOT Scrapfly,
 * which is cancelled). Portals that merely block our direct probe show UP with
 * a "blocks direct" note, because real fetches go through the proxy anyway.
 * Failures also alert through Scraper Health rows. */
import { useCallback, useEffect, useState } from "react";
import { backendFetch } from "../../lib/backend";

interface Probe {
  ok?: boolean;
  http?: number;
  ms?: number;
  err?: string;
  url?: string;
  /** appraiser | permit | service ("county"/"city" in pre-08-14 matrices) */
  group?: string;
  /** direct | needs-proxy | error | down (portals only) */
  reach?: string;
  /** decodo row only: is the residential pool even loaded? */
  residential_pool?: boolean;
}

interface Matrix {
  targets?: Record<string, Probe>;
  down?: string[];
  checked_at?: string;
  every_min?: number;
}

function ago(iso?: string): string {
  if (!iso) return "never";
  const t = Date.parse(iso.endsWith("Z") ? iso : iso + "Z");
  if (!Number.isFinite(t)) return "unknown";
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)}h ago`;
}

interface ProxyTest { ok?: boolean; engine?: string; residential_pool?: boolean; detail?: string }
interface Bandwidth { available?: boolean; kind?: string; used_gb?: number; cap_gb?: number; pct?: number; remaining_gb?: number; month?: string; cycle_until?: string }

export function PortalHealthCard({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [m, setM] = useState<Matrix | null>(null);
  const [bw, setBw] = useState<Bandwidth | null>(null);
  const load = useCallback(async () => {
    try { setM(await backendFetch<Matrix>(token, "/api/scrapers/portal-health", { timeoutMs: 20000 })); } catch { /* transient */ }
    try { setBw(await backendFetch<Bandwidth>(token, "/api/scrapers/bandwidth", { timeoutMs: 20000 })); } catch { /* transient */ }
  }, [token]);
  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);

  // one-tap live proxy probe (Tom 2026-08-14) — confirm the second Decodo is back
  const [pt, setPt] = useState<ProxyTest | null>(null);
  const [ptBusy, setPtBusy] = useState(false);
  const testProxy = useCallback(async () => {
    if (ptBusy) return;
    setPtBusy(true); setPt(null);
    try { setPt(await backendFetch<ProxyTest>(token, "/api/scrapers/proxy-test", { timeoutMs: 40000 })); }
    catch (e) { setPt({ ok: false, detail: e instanceof Error ? e.message : "probe failed" }); }
    finally { setPtBusy(false); }
  }, [token, ptBusy]);

  if (!isAdmin) return null;
  const targets = m?.targets ?? {};
  const rank = (g?: string) => (g === "county" || g === "appraiser" ? 0 : g === "city" || g === "permit" ? 1 : 2);
  const names = Object.keys(targets).sort((a, b) => {
    return rank(targets[a]?.group) - rank(targets[b]?.group) || a.localeCompare(b);
  });
  const downCount = (m?.down ?? []).length;

  return (
    <div className="zfpurge">
      <div className="zfphd">
        <div>
          <b>Permit &amp; appraiser portals</b>
          <div className="sub">
            County appraisers, city permit portals, core data services and the paid proxy path — probed every {m?.every_min ?? 30} min.
            Failures alert via Scraper Health.
          </div>
        </div>
        <div className="zfpacts" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn slim" disabled={ptBusy} onClick={() => void testProxy()}>
            {ptBusy ? "Testing proxy…" : "Test proxy now"}
          </button>
          {m === null ? (
            <span className="sub dim">Loading…</span>
          ) : names.length === 0 ? (
            <span className="sub dim">No sweep yet — the worker runs every {m?.every_min ?? 30} min</span>
          ) : downCount === 0 ? (
            <span className="cmppill done">ALL UP</span>
          ) : (
            <span className="cmppill dead">{downCount} DOWN</span>
          )}
        </div>
      </div>
      {bw?.available && typeof bw.used_gb === "number" && bw.cap_gb ? (() => {
        const pct = Math.min(100, bw.pct ?? 0);
        const col = pct >= 90 ? "#b91c1c" : pct >= 70 ? "#b45309" : "#047857";
        return (
          <div style={{ marginTop: 10 }}>
            <div className="sub" style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span>
                {bw.kind === "decodo"
                  ? <>Residential proxy · this billing cycle (Decodo live{bw.cycle_until ? `, renews ${bw.cycle_until}` : ""})</>
                  : <>Residential proxy · {bw.month || "this month"} (internal meter — set DECODO_API_KEY for the exact Decodo cycle)</>}
              </span>
              <span style={{ color: col, fontWeight: 600 }}>
                {bw.used_gb} / {bw.cap_gb} GB ({pct}%){typeof bw.remaining_gb === "number" ? ` · ${bw.remaining_gb} GB left` : ""}
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 6, background: "var(--hair, #e5e7eb)", overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: col, transition: "width .3s" }} />
            </div>
          </div>
        );
      })() : null}
      {pt ? (
        <div className="cmpnote" style={{ marginTop: 8 }}>
          {pt.ok
            ? `✓ Residential proxy is UP — live fetch succeeded${pt.residential_pool === false ? " (but no pool loaded — going direct)" : ""}.`
            : `⛔ Residential proxy is DOWN${pt.detail ? " — " + pt.detail : ""}. County / city / realtor stay empty until this clears.`}
        </div>
      ) : null}
      {names.length ? (
        <>
          <div className="permittable" style={{ marginTop: 8 }}>
            <div className="permitrow permithead" style={{ gridTemplateColumns: "170px 90px 70px 70px 1fr" }}>
              <span>Site</span><span>Status</span><span>HTTP</span><span>ms</span><span>URL / error</span>
            </div>
            {names.map((n) => {
              const t: Probe = targets[n] ?? {};
              return (
                <div key={n} className="permitrow" style={{ gridTemplateColumns: "170px 90px 70px 70px 1fr" }}>
                  <span>{n}{rank(t.group) === 0 ? " ·apprsr" : rank(t.group) === 1 ? "" : " ·svc"}</span>
                  <span><span className={"cmppill " + (t.ok ? "done" : "dead")}>{t.ok ? "UP" : "DOWN"}</span></span>
                  <span>{t.http || "—"}</span>
                  <span>{typeof t.ms === "number" ? t.ms : "—"}</span>
                  <span className="pdesc">
                    {t.reach === "needs-proxy" ? <span className="sub dim">blocks direct probes — real fetches go via proxy · </span> : null}
                    {t.err && t.ok ? <span className="sub dim">{t.err} · </span> : null}
                    {t.err && !t.ok ? <span style={{ color: "var(--red, #b91c1c)" }}>{t.err}</span> : null}
                    {t.err && !t.ok && t.url ? " · " : null}
                    {t.url ? <a className="dlink" href={t.url} target="_blank" rel="noreferrer">{t.url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}</a> : null}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="sub dim" style={{ marginTop: 6 }}>Last sweep: {ago(m?.checked_at)}</div>
        </>
      ) : null}
    </div>
  );
}
